// Butonul „+" care stă pe crosshair-ul graficului, lângă eticheta preţului.
//
// Pe telefon, crosshair-ul bibliotecii rămâne pe ecran după ce ridici degetul — asta e
// purtarea ei implicită, şi e cea bună: arăţi un preţ, apoi te uiţi la el în linişte.
// Butonul acesta călăreşte pe el şi deschide ce se poate face cu preţul arătat.
//
// De ce un buton **HTML** şi nu un desen pe pânză: o pânză care prinde atingeri fură
// gesturile graficului — nu mai poţi nici muta, nici apropia. Un singur element de
// douăzeci şi opt de pixeli, aşezat deasupra, lasă tot restul ecranului graficului şi
// ia doar apăsarea care e chiar pe el. Aşa face şi TradingView.
//
// Butonul nu apare când sunt deschise uneltele de desen: acolo degetul desenează, iar
// un buton care se plimbă peste linia pe care o tragi ar fi în drum.

import { el } from '../core/util.js';
import { icon } from '../ui/icons.js';

export class VizorButon {
  /**
   * @param {Chart} chart  instanţa noastră de grafic
   * @param {(pret: number, punct: {x: number, y: number}) => void} laApasare
   */
  constructor(chart, laApasare) {
    this.chart = chart;
    this.laApasare = laApasare;
    this.pret = null;
    this.ascuns = false;
    this.peButon = false;

    this.el = el('button', {
      class: 'chart-vizor-buton',
      title: 'Что сделать с этой ценой',
      type: 'button',
    }, [icon('plus', 13)]);
    this.el.style.display = 'none';

    // `pointerdown`, nu `click`: pe telefon, biblioteca prinde atingerea şi mută
    // crosshair-ul înainte ca apăsarea să apuce să se încheie, iar `click` n-ar mai
    // veni niciodată. Oprim propagarea ca graficul să nu ia acelaşi gest drept mutare.
    const apasa = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.pret == null) return;
      const r = this.el.getBoundingClientRect();
      this.laApasare(this.pret, { x: r.left + r.width / 2, y: r.top + r.height / 2 });
    };
    this.el.addEventListener('pointerdown', apasa);

    // Cat timp degetul sau cursorul sta pe buton, graficul nu mai primeste miscarea si
    // isi stinge crosshair-ul — adica butonul ar disparea exact sub degetul care il
    // apasa. Cat suntem pe el, nu ascultam vestea aceea.
    this.el.addEventListener('pointerenter', () => { this.peButon = true; });
    this.el.addEventListener('pointerleave', () => { this.peButon = false; });

    this.host = chart.mainEl;
    this.host.append(this.el);

    // Metodele private nu se pot lega cu `bind`, deci ţinem un înveliş obişnuit —
    // aceeaşi referinţă la abonare şi la dezabonare, altfel dezabonarea n-ar prinde.
    this.laMiscare = (param) => this.#urmareste(param);
    chart.chart.subscribeCrosshairMove(this.laMiscare);
  }

  /** Crosshair-ul s-a mutat: mutăm butonul după el sau îl ascundem. */
  #urmareste(param) {
    if (this.ascuns) { this.#stinge(); return; }
    if (!param?.point) { if (!this.peButon) this.#stinge(); return; }

    const pret = this.chart.candles?.coordinateToPrice(param.point.y);
    if (pret == null || !Number.isFinite(pret)) { this.#stinge(); return; }

    this.pret = pret;
    // Lipit de eticheta preţului de pe axă, în stânga ei — acolo îl caută ochiul,
    // fiindcă acolo scrie preţul despre care e vorba.
    const latimeAxa = this.chart.chart.priceScale('right').width() || 0;
    this.el.style.right = `${Math.round(latimeAxa + 6)}px`;
    this.el.style.top = `${Math.round(param.point.y)}px`;
    this.el.style.display = '';
  }

  /**
   * Ascunde butonul, dar tine minte preţul.
   *
   * Sunt două lucruri deosebite: dacă se vede, şi despre ce preţ e vorba. Le-am legat
   * întâi laolaltă şi apăsarea cădea în gol — graficul stingea crosshair-ul chiar în
   * clipa în care degetul ajungea pe buton. Apăsarea poate veni doar pe un buton care
   * se vede, deci preţul reţinut e mereu cel arătat ultima dată.
   */
  #stinge() {
    this.el.style.display = 'none';
  }

  /** Ascuns cât timp sunt deschise uneltele de desen. */
  setAscuns(ascuns) {
    this.ascuns = !!ascuns;
    if (this.ascuns) this.#stinge();
  }

  destroy() {
    try { this.chart.chart.unsubscribeCrosshairMove(this.laMiscare); } catch { /* graficul poate fi deja închis */ }
    this.el.remove();
  }
}
